import type {
  EngageEnrollment,
  EngageSequence,
  EngageStep,
} from '@/store/features/engage.feature';

/**
 * Which step each running lead is on, read back from the `step_label` the
 * sending engine writes on the enrollment. The label is prose ("Scheduled:
 * Email", "Waiting 2 business day(s)", "Task: Call Ada"), so a step is only
 * identified when it is the one step of its kind the label can mean. A label
 * two steps could produce is left unplaced: a count on the canvas is exact or
 * absent, never a guess.
 */

/** Only a lead that is running, or waiting for launch, is on a step. */
export const ON_A_STEP = new Set(['Pending', 'Active']);

/** Written before the first step runs, or while the lead waits for launch. */
const AT_START = new Set([
  'Starts at launch',
  'First email queued',
  'First step queued',
]);

/** The engine holds an email step here without naming it. */
const DAILY_LIMIT = 'Waiting for the daily sending limit to reset';

/** Labels an email step writes around its title. */
const EMAIL_LABELS = [
  /^Sent: (.+)$/,
  /^Sent \(unconfirmed\): (.+)$/,
  /^Scheduled: (.+)$/,
  /^Sending: (.+)$/,
  /^Could not send (.+)\. Trying again$/,
  /^Stopped: (.+) could not be sent$/,
];
/** Both the engine's older "day(s)" and its plural-aware wording. */
const WAIT_LABEL = /^Waiting (\d+) business (?:days?|day\(s\))$/;

/**
 * A step label as a person reads it. The engine used to write "Waiting 2
 * business day(s)"; rows it wrote then still carry that, so it is spelled out
 * here ("1 business day", "2 business days"). Any other label is unchanged.
 */
export function stepLabelText(label: string | null | undefined): string {
  if (!label) return '';
  return label.replace(
    /^(Waiting (\d+) business day)\(s\)$/,
    (_all, start: string, count: string) =>
      Number(count) === 1 ? start : start + 's'
  );
}
/** What a LinkedIn step writes once its action is done. Skips name no kind. */
const LINKEDIN_LABELS = [
  ['Visited LinkedIn profile', 'linkedin_visit'],
  ['Sent LinkedIn invitation', 'linkedin_invite'],
  ['Sent LinkedIn message', 'linkedin_message'],
  // A handwritten note (cleanse-api engine.HANDWRITTEN_DONE).
  ['Mailed handwritten note', 'handwritten_note'],
] as const;
const TASK_LABEL = /^Task: (.+)$/;

/** The title a published step carries when the builder left it blank. */
function emailTitle(step: EngageStep): string {
  return step.title || 'Email';
}

/** The first non-empty line of a task step's instructions, trimmed. */
function firstLine(text: string | null): string {
  return (
    (text ?? '')
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.length > 0) ?? ''
  );
}

/**
 * A task's name is that first line with the variables filled in, so match it
 * as a pattern: every `{{token}}` may stand for anything.
 */
function nameMatchesInstructions(name: string, instructions: string | null) {
  const line = firstLine(instructions);
  if (!line) return name.startsWith('Task for ');
  // The engine cuts a long first line to 77 characters plus an ellipsis; a
  // cut name cannot be matched back to its line.
  if (name.endsWith('...')) return false;
  const pattern = line
    .split(/\{\{[^}]*\}\}/)
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  return new RegExp('^' + pattern + '$').test(name);
}

function only(candidates: EngageStep[]): EngageStep | undefined {
  return candidates.length === 1 ? candidates[0] : undefined;
}

/**
 * The step a label points at, or undefined when the label names no step or
 * could name more than one.
 */
export function stepForLabel(
  steps: EngageStep[],
  label: string
): EngageStep | undefined {
  if (AT_START.has(label))
    return steps.find((step) => step.kind !== 'wait') ?? steps[0];
  if (label === DAILY_LIMIT)
    return only(steps.filter((step) => step.kind === 'email'));
  for (const pattern of EMAIL_LABELS) {
    const match = pattern.exec(label);
    if (match)
      return only(
        steps.filter(
          (step) => step.kind === 'email' && emailTitle(step) === match[1]
        )
      );
  }
  for (const [prefix, kind] of LINKEDIN_LABELS)
    if (label.startsWith(prefix))
      return only(steps.filter((step) => step.kind === kind));
  const wait = WAIT_LABEL.exec(label);
  if (wait) {
    const days = Number(wait[1]);
    return only(
      steps.filter((step) => step.kind === 'wait' && step.wait_days === days)
    );
  }
  const task = TASK_LABEL.exec(label);
  if (task) {
    const name = task[1];
    const tasks = steps.filter((step) => step.kind === 'task');
    const manual = tasks.filter((step) => step.task_type !== 'Call');
    const written = manual.filter((step) =>
      nameMatchesInstructions(name, step.instructions)
    );
    // A call task is always named "Call <who>"; the rest carry their first
    // line of instructions, or "Task for <who>" when there is none.
    if (name.startsWith('Call '))
      return only([
        ...tasks.filter((step) => step.task_type === 'Call'),
        ...written,
      ]);
    return only(manual) ?? only(written);
  }
  return undefined;
}

/**
 * The step a running lead is on, or undefined when it cannot be placed. The
 * engine records the step's index directly; the label is the fallback for runs
 * that started before it did. The index is a position in the lead's pinned
 * revision, so it only matches these steps when that revision is the published
 * one on screen (not an older one, not a draft).
 */
export function stepOfEnrollment(
  steps: EngageStep[],
  enrollment: Pick<EngageEnrollment, 'step_index' | 'step_label' | 'version'>,
  sequence: Pick<EngageSequence, 'version' | 'draft'>
): EngageStep | undefined {
  const byIndex =
    enrollment.step_index === null ||
    enrollment.step_index === undefined ||
    enrollment.version !== sequence.version ||
    sequence.draft
      ? undefined
      : steps.find((step) => step.order_index === enrollment.step_index);
  return byIndex ?? stepForLabel(steps, enrollment.step_label);
}

/**
 * How many leads sit on each step right now, keyed by step id. Waits count
 * under their own id; the canvas folds a wait into the card that follows it.
 * Steps no lead can be placed on are absent.
 */
export function leadsPerStep(
  steps: EngageStep[],
  enrollments: EngageEnrollment[],
  sequence: Pick<EngageSequence, 'version' | 'draft'>
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const enrollment of enrollments) {
    if (!ON_A_STEP.has(enrollment.status)) continue;
    const step = stepOfEnrollment(steps, enrollment, sequence);
    if (step) counts[step.id] = (counts[step.id] ?? 0) + 1;
  }
  return counts;
}
